/* AI / Lokale KI (LiteLLM) — Superadmin.
 *
 * „AI aktivieren" als orchestrierter Job (Maintenance-Splash + LiteLLM-Key
 * pro Cloud + ExApp-VM + Assistant/integration_openai + Context Chat/Agent).
 * Pollt den Status während der Provisionierung und zeigt Key/Modelle/MCP/VM.
 */
import React, { useState } from "react";
import useSWR from "swr";
import { api, formatApiError } from "@/lib/api";
import { KV } from "@/components/Atoms";
import { Link } from "react-router-dom";
import {
    Sparkles, KeyRound, Server, Search, BrainCircuit, Globe2,
    CheckCircle2, XCircle, Loader2, AlertTriangle, Cpu, Wrench,
} from "lucide-react";

const fetcher = (u) => api.get(u).then((r) => r.data);

const SIZES = [
    { id: "s", label: "S — 2 vCPU · 4 GB · 40 GB" },
    { id: "m", label: "M — 4 vCPU · 8 GB · 80 GB" },
    { id: "l", label: "L — 8 vCPU · 16 GB · 160 GB" },
];

const STATUS = {
    off: { cls: "cm-badge-neutral", label: "AI off", icon: Sparkles },
    provisioning: { cls: "cm-badge-info", label: "Provisioning…", icon: Loader2, spin: true },
    active: { cls: "cm-badge-success", label: "AI active", icon: CheckCircle2 },
    failed: { cls: "cm-badge-danger", label: "Failed", icon: XCircle },
    disabling: { cls: "cm-badge-warning", label: "Disabling…", icon: Loader2, spin: true },
    repairing: { cls: "cm-badge-info", label: "Repairing…", icon: Loader2, spin: true },
    disabled: { cls: "cm-badge-neutral", label: "Disabled", icon: XCircle },
};

export default function CloudAiPanel({ cloudId, cloud, onChange }) {
    const { data, mutate } = useSWR(
        `/api/admin/clouds/${cloudId}/ai`, fetcher, { refreshInterval: 4000 });
    const [size, setSize] = useState(data?.exapp_size_id || "m");
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState(null);

    const status = data?.status || "off";
    const st = STATUS[status] || STATUS.off;
    const StIcon = st.icon;
    const running = status === "provisioning" || status === "disabling"
        || status === "repairing";
    const isActive = status === "active";
    const key = data?.key || null;
    const notActive = cloud?.status !== "active";

    const call = async (fn) => {
        setBusy(true); setErr(null);
        try {
            await fn();
            await mutate();
            if (onChange) await onChange();
        } catch (e) { setErr(formatApiError(e)); }
        setBusy(false);
    };

    const enable = () => call(async () =>
        api.post(`/api/admin/clouds/${cloudId}/ai/enable`, { size_id: size }));
    const repair = () => call(async () =>
        api.post(`/api/admin/clouds/${cloudId}/ai/repair`));
    const disable = () => {
        if (!window.confirm(
            "Disable AI?\n\nThe LiteLLM key will be blocked and the assistant " +
            "switched off. The ExApp VM remains in place and can be removed " +
            "separately via AppAPI."))
            return;
        call(async () => api.post(`/api/admin/clouds/${cloudId}/ai/disable`));
    };

    return (
        <div className="cm-card p-5" data-testid="cloud-ai-panel">
            <div className="cm-h3 mb-4 flex items-center justify-between">
                <span className="flex items-center gap-1.5">
                    <BrainCircuit size={14} className="text-cm-brand" />
                    AI / Local AI · LiteLLM
                </span>
                <span className={`cm-badge ${st.cls} text-[11px] flex items-center gap-1`}
                    data-testid="cloud-ai-status">
                    <StIcon size={11} className={st.spin ? "animate-spin" : ""} />
                    {st.label}
                </span>
            </div>

            <p className="text-xs text-zinc-500 mb-4 leading-relaxed">
                Enables the Nextcloud assistant via the central LiteLLM gateway.
                Each cloud receives its own <span className="text-zinc-300">virtual key</span> (separate
                usage billing) plus a <span className="text-zinc-300">WebSearch MCP token</span>.
                A dedicated <span className="text-zinc-300">ExApp VM</span> (deploy daemon) is
                provisioned for Context Chat / RAG. While activating, the cloud is in maintenance mode.
            </p>

            {err && (
                <div className="mb-3 text-xs text-[#fb6f86] bg-[#F43F5E]/10 border border-[#F43F5E]/40 p-2 flex items-start gap-1.5"
                    data-testid="cloud-ai-error">
                    <AlertTriangle size={13} className="mt-0.5 shrink-0" /> {err}
                </div>
            )}

            {/* --- Aktive Konfiguration --- */}
            {isActive && key && (
                <div className="space-y-3 mb-4" data-testid="cloud-ai-details">
                    <div className="bg-zinc-950 border border-zinc-800 p-3">
                        <div className="cm-label mb-1.5 flex items-center gap-1.5">
                            <KeyRound size={11} /> LiteLLM Virtual Key
                        </div>
                        <div className="font-mono text-xs text-zinc-200">{key.masked_token || key.key_prefix}</div>
                        <div className="font-mono text-[11px] text-zinc-500 mt-1">
                            {(key.monthly_token_limit || 0) > 0
                                ? `Limit ${Number(key.monthly_token_limit).toLocaleString()} tok/mo · `
                                : "no budget limit · "}
                            used {Number(key.usage_tokens || 0).toLocaleString()}
                        </div>
                        <div className="mt-2 flex flex-wrap gap-1">
                            {(key.models_allowed || []).map((m) => (
                                <span key={m} className="cm-badge cm-badge-neutral text-[10px] font-mono">{m}</span>
                            ))}
                        </div>
                    </div>
                    <div className="grid grid-cols-2 gap-px bg-zinc-800">
                        <div className="bg-zinc-950 p-3">
                            <div className="cm-label mb-1 flex items-center gap-1.5">
                                <Search size={11} /> WebSearch-MCP
                            </div>
                            <div className="font-mono text-xs text-zinc-300">
                                {key.mcp_set ? (key.mcp_token_prefix || "set") + "…" : "—"}
                            </div>
                        </div>
                        <div className="bg-zinc-950 p-3">
                            <div className="cm-label mb-1 flex items-center gap-1.5">
                                <Server size={11} /> ExApp-VM
                            </div>
                            <div className="font-mono text-xs text-zinc-300">
                                {(data.appapi_vm_ip || "—")}{" "}
                                <span className="text-zinc-600">({(data.exapp_size_id || "m").toUpperCase()})</span>
                            </div>
                        </div>
                    </div>
                    <div className="flex flex-wrap gap-2">
                        <FeatureFlag ok={data.context_chat} label="Context Chat / RAG" />
                        <FeatureFlag ok={data.context_agent} label="Context Agent · WebSearch" />
                    </div>
                </div>
            )}

            {/* --- ExApp-VM-Größe (nur wenn nicht aktiv/laufend) --- */}
            {!isActive && !running && (
                <div className="mb-4">
                    <label className="cm-label mb-1.5 flex items-center gap-1.5" htmlFor="ai-size">
                        <Cpu size={11} /> ExApp VM size
                    </label>
                    <select
                        id="ai-size"
                        data-testid="cloud-ai-size-select"
                        value={size}
                        onChange={(e) => setSize(e.target.value)}
                        disabled={busy}
                        className="w-full bg-zinc-950 border border-zinc-800 text-xs text-zinc-200 px-2 py-2 focus:border-[#CCFF00]/50 outline-none font-mono"
                    >
                        {SIZES.map((s) => (
                            <option key={s.id} value={s.id}>{s.label}</option>
                        ))}
                    </select>
                </div>
            )}

            {running && (
                <div className="mb-4 text-xs text-cm-brand flex items-center gap-2" data-testid="cloud-ai-running">
                    <Loader2 size={13} className="animate-spin" />
                    Job running — progress in{" "}
                    <Link to={`/clouds/${cloudId}`} className="underline hover:text-[#d6ff33]">Job-Log</Link>.
                </div>
            )}

            {/* --- Aktionen --- */}
            <div className="flex flex-wrap gap-2 items-center">
                {!isActive && !running && (
                    <button
                        data-testid="cloud-ai-enable-btn"
                        className="cm-btn-primary text-xs flex items-center gap-1.5"
                        disabled={busy || notActive}
                        title={notActive ? "Cloud must be active" : "Provision full AI integration"}
                        onClick={enable}
                    >
                        <Sparkles size={12} /> {status === "failed" ? "Retry" : "Enable AI"}
                    </button>
                )}
                {isActive && (
                    <>
                        <button
                            data-testid="cloud-ai-repair-btn"
                            className="cm-btn-secondary text-xs flex items-center gap-1.5"
                            disabled={busy}
                            title="Idempotently re-set and verify the LiteLLM key & Nextcloud configuration (without re-provisioning the ExApp VM)"
                            onClick={repair}
                        >
                            <Wrench size={12} /> Repair AI
                        </button>
                        <button
                            data-testid="cloud-ai-disable-btn"
                            className="cm-btn-danger text-xs flex items-center gap-1.5"
                            disabled={busy}
                            onClick={disable}
                        >
                            <XCircle size={12} /> Disable AI
                        </button>
                    </>
                )}
                {notActive && !isActive && (
                    <span className="text-xs text-zinc-500">Cloud must be active.</span>
                )}
            </div>

            <div className="mt-3 text-[11px] text-zinc-600 flex items-center gap-1">
                <Globe2 size={10} />
                Gateway &amp; master key under{" "}
                <Link to="/infrastructure" className="underline hover:text-zinc-400">Infrastructure → Settings</Link>
                {" "}(<span className="font-mono">litellm_master_key</span>).
            </div>
        </div>
    );
}

function FeatureFlag({ ok, label }) {
    return (
        <span className={`cm-badge text-[10px] flex items-center gap-1 ${ok ? "cm-badge-success" : "cm-badge-neutral"}`}>
            {ok ? <CheckCircle2 size={10} /> : <XCircle size={10} />} {label}
        </span>
    );
}
